import { useEffect, useState } from 'react';
import { apiGetPaymentsOverview } from '~/lib/api/payments';
import { apiCreateFunnel, apiGetFunnel, apiListFunnels, type Funnel } from '~/lib/api/funnels';
import { classNames } from '~/utils/classNames';
import { isValidCurrency } from '~/components/workbench/tools/payments/payments-model';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { apiErrorMessage, funnelStatusMeta, slugify } from './funnels-model';

/** Funnel rows plus their step counts (the list endpoint has no counts, so each detail is fetched). */
async function loadFunnels(appId: string): Promise<{ funnel: Funnel; steps: number | null }[]> {
  const funnels = await apiListFunnels(appId);
  const details = await Promise.allSettled(funnels.map((f) => apiGetFunnel(appId, f.id)));

  return funnels.map((funnel, i) => {
    const d = details[i];
    return { funnel, steps: d.status === 'fulfilled' ? d.value.steps.length : null };
  });
}

export function FunnelsList({ appId, onOpen }: { appId: string; onOpen: (funnelId: string) => void }) {
  const funnels = useResource(() => loadFunnels(appId), appId);
  const [creating, setCreating] = useState(false);
  const empty = funnels.data !== null && funnels.data.length === 0;

  const addButton = !creating && !empty && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New funnel
    </button>
  );

  return (
    <Section title="Funnels" icon="i-ph:funnel" aside={addButton}>
      {creating && (
        <NewFunnelForm
          appId={appId}
          onDone={(id) => {
            setCreating(false);

            if (id) {
              onOpen(id);
            }
          }}
        />
      )}

      {funnels.loading && !funnels.data ? (
        <SkeletonCard lines={3} />
      ) : funnels.error ? (
        <LoadError message="Could not load funnels." onRetry={() => void funnels.reload()} />
      ) : empty ? (
        !creating && (
          <div className={classNames(CARD, 'px-5 py-7 text-center')}>
            <div className="i-ph:funnel mx-auto mb-3 text-2xl text-faint" />
            <p className="ui-serif text-[20px] leading-tight text-primary">No funnels yet</p>
            <p className="mx-auto mt-2 max-w-[290px] text-[12px] leading-relaxed text-muted">
              Ask the agent to build one (“a funnel for my course with a ₹199 bump”), or start one here and lay out the
              steps yourself.
            </p>
            <button onClick={() => setCreating(true)} className={classNames(BTN_SECONDARY, 'mt-4')}>
              <div className="i-ph:plus text-[13px]" />
              New funnel
            </button>
          </div>
        )
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {funnels.data!.map(({ funnel, steps }) => {
            const status = funnelStatusMeta(funnel.status);

            return (
              <button
                key={funnel.id}
                type="button"
                onClick={() => onOpen(funnel.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[13px] font-medium text-primary">{funnel.name}</span>
                    <Pill tone={status.tone} label={status.label} />
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted">
                    <span className="font-geist-mono">/{funnel.slug}</span>
                    <span aria-hidden>·</span>
                    <span>{steps === null ? '—' : `${steps} step${steps === 1 ? '' : 's'}`}</span>
                    <span aria-hidden>·</span>
                    <span className="font-geist-mono">{funnel.currency}</span>
                  </div>
                </div>
                <div className="i-ph:caret-right shrink-0 text-[12px] text-faint" />
              </button>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function NewFunnelForm({ appId, onDone }: { appId: string; onDone: (funnelId: string | null) => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [currency, setCurrency] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const payments = useResource(() => apiGetPaymentsOverview(appId), appId);
  const defaultCurrency = payments.data?.settings.currency;

  // Default to the Payments currency once it loads (unless the owner typed one).
  useEffect(() => {
    if (defaultCurrency) {
      setCurrency((c) => c || defaultCurrency);
    }
  }, [defaultCurrency]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const code = currency.trim().toUpperCase();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Give the funnel a name.');
      return;
    }

    if (!isValidCurrency(code)) {
      setError('Use a three-letter currency code, e.g. INR or USD.');
      return;
    }

    setSaving(true);

    try {
      const created = await apiCreateFunnel(appId, {
        name: name.trim(),
        currency: code,
        ...(effectiveSlug ? { slug: effectiveSlug } : {}),
      });
      onDone(created.funnel.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this funnel.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Course launch"
          className="ui-input"
        />
      </Field>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Field label="Slug" hint="The funnel's address on your site.">
            <input
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              placeholder="course-launch"
              spellCheck={false}
              className="ui-input-mono"
            />
          </Field>
        </div>
        <div className="w-20">
          <Field label="Currency">
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              maxLength={3}
              spellCheck={false}
              className="ui-input-mono uppercase"
            />
          </Field>
        </div>
      </div>
      <p className="text-[11px] leading-relaxed text-muted">
        New funnels start as drafts: visible in the preview and test checkouts only.
      </p>
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className="ui-btn-accent !px-4 !py-2 !text-[12px]">
          {saving && <Spinner />}
          {saving ? 'Creating…' : 'Create funnel'}
        </button>
        <button type="button" onClick={() => onDone(null)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
